/**
 * useTableEditing — Manages inline cell editing state for DataTable.
 *
 * Tracks which cell (entityId × propertyName) is currently in edit mode
 * and prevents multiple simultaneous edits. The hook is designed to be
 * instantiated inside DataTable and passed down via render props or context.
 *
 * Navigation helpers (onTabNext / onEnterNext) are computed by the table
 * and passed into EditableCell directly.
 */

import { useCallback, useState } from 'react';

import type { DataTableCellState } from '../types';

export interface TableEditingState {
  /** Currently active edit cell, or null when nothing is being edited */
  editingCell: DataTableCellState | null;
  /** Enter edit mode for the given cell. Commits any previously open cell first. */
  startEdit: (entityId: string, propertyName: string) => void;
  /**
   * Clear edit mode (called after a successful save or on blur).
   *
   * Pass the identity of the cell whose edit completed. Saves commit
   * asynchronously (blur is RAF-delayed and the save itself awaits the
   * network), so by the time a commit lands the user may already be editing
   * a DIFFERENT cell — clearing unconditionally would close that newer
   * editor mid-edit. With an identity, the clear is a no-op unless that
   * exact cell is still the active one. Omitting the identity clears
   * unconditionally (legacy behaviour, for callers that know no newer edit
   * can exist).
   */
  commitEdit: (cell?: DataTableCellState) => void;
  /** Clear edit mode and revert value (called on Escape). Same identity
   *  semantics as `commitEdit`. */
  cancelEdit: (cell?: DataTableCellState) => void;
  /** Returns true when the given (entityId, propertyName) pair is in edit mode. */
  isEditing: (entityId: string, propertyName: string) => boolean;
}

/**
 * Hook for managing keyboard-navigable inline cell editing in DataTable.
 *
 * Only one cell can be in edit mode at a time. Calling startEdit while
 * another cell is open implicitly commits it (the blur handler on the
 * previously-open input fires before the click on the new cell).
 */
export function useTableEditing(): TableEditingState {
  const [editingCell, setEditingCell] = useState<DataTableCellState | null>(
    null
  );

  const startEdit = useCallback((entityId: string, propertyName: string) => {
    setEditingCell({ entityId, propertyName });
  }, []);

  // Functional updates so the identity check runs against the LATEST editing
  // cell, not the one captured when the async save started.
  const clearIfMatching = useCallback((cell?: DataTableCellState) => {
    setEditingCell(current => {
      if (current === null) {
        return null;
      }
      if (
        cell !== undefined &&
        (current.entityId !== cell.entityId ||
          current.propertyName !== cell.propertyName)
      ) {
        // A different cell entered edit mode while this one's save was in
        // flight — leave it alone.
        return current;
      }
      return null;
    });
  }, []);

  const commitEdit = useCallback(
    (cell?: DataTableCellState) => clearIfMatching(cell),
    [clearIfMatching]
  );

  const cancelEdit = useCallback(
    (cell?: DataTableCellState) => clearIfMatching(cell),
    [clearIfMatching]
  );

  const isEditing = useCallback(
    (entityId: string, propertyName: string) =>
      editingCell?.entityId === entityId &&
      editingCell?.propertyName === propertyName,
    [editingCell]
  );

  return { editingCell, startEdit, commitEdit, cancelEdit, isEditing };
}
